import { CommonModule, DatePipe } from '@angular/common';
import { NgModule } from '@angular/core';
import { FileUploaderModule } from '@app/shared/components/file-uploader/file-uploader.module';
import { FilterWidgetModule } from '@app/shared/components/filter-widget/filter-widget.module';
import { FormModule } from '@app/shared/components/form/form.module';
import { PaginatorModule } from '@app/shared/components/paginator/paginator.module';
import { SearchWidgetModule } from '@app/shared/components/search-widget/search-widget.module';
import { SortWidgetModule } from '@app/shared/components/sort-widget/sort-widget.module';
import { ResponsiveTableModule } from '@app/shared/components/table/responsive-table.module';
import {
  AccordionComponent,
  AlertComponent,
  BadgeComponent,
  ButtonComponent,
  CardComponent,
  ContextMenuComponent,
  DragDropModule,
  IconComponent,
  ListGroupDirective,
  ListGroupItemDirective,
  ModalComponent,
  OverlayComponent,
  ProgressComponent,
  SpinnerComponent,
  TabviewComponent,
  ToolbarComponent,
  TooltipDirective,
} from '@teorema/patron';
import { MarkdownModule } from 'ngx-markdown';
import { PopoverModule } from 'primeng/popover';
import { TooltipModule } from 'primeng/tooltip';
import {
  FilenameModule,
  FilenamePipe,
  HighlightModule,
  SortModule,
  SortPipe,
  TimeToDateModule,
  TruncateModule,
} from 'src/app/shared/pipes';
import { KeyboardModule } from '../keyboard/keyboard.module';
import { DifficultyPipe } from './pipes/difficulty.pipe';
import { TicketLogAttendanceTypePipe } from './pipes/ticket-log-attendance-type.pipe';
import { TicketLogDepartmentTypePipe } from './pipes/ticket-log-department-type.pipe';
import { TicketLogTimePipe } from './pipes/ticket-log-time.pipe';
import { TicketLogTypePipe } from './pipes/ticket-log-type.pipe';
import { TicketDropService } from './services/ticket-drop.service';
import { AbstractSprintComponent } from './sprint/abstract-sprint/abstract-sprint.component';
import { SprintColumnComponent } from './sprint/sprint-column/sprint-column.component';
import { SprintContextMenuComponent } from './sprint/sprint-context-menu/sprint-context-menu.component';
import { SprintInnerColumnComponent } from './sprint/sprint-inner-column/sprint-inner-column.component';
import { SprintComponent } from './sprint/sprint.component';
import { TicketCardContainerComponent } from './sprint/ticket-card-container/ticket-card-container.component';
import { TicketLogHistoryComponent } from './sprint/ticket-log-history/ticket-log-history.component';
import { TicketModalComponent } from './sprint/ticket-modal/ticket-modal.component';
import { TicketCenterRouting } from './ticket-center.routing';
import { EditTicketComponent } from './ticket/edit-ticket/edit-ticket.component';
import { NotificationsDropdownComponent } from './ticket/notifications-dropdown/notifications-dropdown.component';
import { RecentlyViewedTicketsComponent } from './ticket/recently-viewed-tickets/recently-viewed-tickets.component';
import { SearchTicketByNumberWidgetComponent } from './ticket/search-ticket-by-number-widget/search-ticket-by-number-widget.component';
import { TaskBulkUpdateComponent } from './ticket/tasks/task-bulk-update/task-bulk-update.component';
import { TasksComponent } from './ticket/tasks/tasks.component';
import { TcTicketListComponent } from './ticket/tc-ticket-list/tc-ticket-list.component';
import { NewTicketComponent } from './ticket/ticket-create/new-ticket.component';
import { TicketDocumentsComponent } from './ticket/ticket-documents/ticket-documents.component';
import { SystemPercentagesComponent } from './ticket/ticket-logs-modal/system-percentages/system-percentages.component';
import { TicketLogsModalComponent } from './ticket/ticket-logs-modal/ticket-logs-modal.component';
import { RequirementsTabComponent } from './ticket/ticket-show/ticket-show-body/tabs/ts-dev-tab/tabs/requirements-tab/requirements-tab.component';
import { RevisionsTabComponent } from './ticket/ticket-show/ticket-show-body/tabs/ts-dev-tab/tabs/revisions-tab/revisions-tab.component';
import { TsDevTabComponent } from './ticket/ticket-show/ticket-show-body/tabs/ts-dev-tab/ts-dev-tab.component';
import { TsDocumentationsTabComponent } from './ticket/ticket-show/ticket-show-body/tabs/ts-documentations-tab/ts-documentations-tab.component';
import { TsInfoTabComponent } from './ticket/ticket-show/ticket-show-body/tabs/ts-info-tab/ts-info-tab.component';
import { TsMainTabComponent } from './ticket/ticket-show/ticket-show-body/tabs/ts-main-tab/ts-main-tab.component';
import { TsOthersTabComponent } from './ticket/ticket-show/ticket-show-body/tabs/ts-others-tab/ts-others-tab.component';
import { TicketShowBodyComponent } from './ticket/ticket-show/ticket-show-body/ticket-show-body.component';
import { TsBodyHeaderComponent } from './ticket/ticket-show/ticket-show-body/ts-body-header/ts-body-header.component';
import { TicketShowHeaderComponent } from './ticket/ticket-show/ticket-show-header/ticket-show-header.component';
import { TsSaveButtonComponent } from './ticket/ticket-show/ts-save-button/ts-save-button.component';
import { UpdateAssistantComponent } from './ticket/update-assistant/update-assistant.component';
import { TcTicketListBetaComponent } from './ticket/tc-ticket-list-beta/tc-ticket-list-beta.component';
import { TicketDetailModalComponent } from '../../features/v3ndor/components/ticket-detail-modal/ticket-detail-modal.component';
import { TimeEntryHistoryModalComponent } from '../../features/v3ndor/components/time-entry-history-modal/time-entry-history-modal.component';
import { TimeEntryMetricsModalComponent } from '../../features/v3ndor/components/time-entry-metrics-modal/time-entry-metrics-modal.component';
import { TicketLogCardComponent } from './sprint/ticket-log-history/ticket-log-card/ticket-log-card.component';
import { TicketLogActiveFiltersComponent } from './sprint/ticket-log-history/ticket-log-active-filters/ticket-log-active-filters.component';
import { TicketLogFilterPanelComponent } from './sprint/ticket-log-history/ticket-log-filter-panel/ticket-log-filter-panel.component';
import { ContextMenuModule } from 'primeng/contextmenu';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { SavedFilterChipsComponent } from '../../features/v3ndor/components/saved-filter-chips/saved-filter-chips.component';

@NgModule({
  declarations: [
    SprintComponent,
    NewTicketComponent,
    EditTicketComponent,
    TicketModalComponent,
    TicketShowBodyComponent,
    TicketShowHeaderComponent,
    AbstractSprintComponent,
    SprintColumnComponent,
    SprintInnerColumnComponent,
    TicketCardContainerComponent,
    TsSaveButtonComponent,
    TsMainTabComponent,
    TsInfoTabComponent,
    TsBodyHeaderComponent,
    TsOthersTabComponent,
    TsDevTabComponent,
    RequirementsTabComponent,
    RevisionsTabComponent,
    SprintContextMenuComponent,
    TicketLogsModalComponent,
    TicketLogTimePipe,
    TicketLogAttendanceTypePipe,
    TicketLogTypePipe,
    TicketLogDepartmentTypePipe,
    TasksComponent,
    TaskBulkUpdateComponent,
    DifficultyPipe,
    TicketDocumentsComponent,
    RecentlyViewedTicketsComponent,
    TicketLogHistoryComponent,
    TcTicketListComponent,
    TcTicketListBetaComponent,
    UpdateAssistantComponent,
    SystemPercentagesComponent,
    SearchTicketByNumberWidgetComponent,
    NotificationsDropdownComponent,
  ],
  imports: [
    ToolbarComponent,
    CommonModule,
    TicketCenterRouting,
    BadgeComponent,
    FormModule,
    TabviewComponent,
    FileUploaderModule,
    SpinnerComponent,
    AccordionComponent,
    CardComponent,
    IconComponent,
    ProgressComponent,
    ButtonComponent,
    FilterWidgetModule,
    ModalComponent,
    ContextMenuComponent,
    DragDropModule,
    FilenameModule,
    SortModule,
    TimeToDateModule,
    PaginatorModule,
    SearchWidgetModule,
    SortWidgetModule,
    ResponsiveTableModule,
    HighlightModule,
    TruncateModule,
    AlertComponent,
    OverlayComponent,
    ListGroupDirective,
    ListGroupItemDirective,
    TooltipDirective,
    KeyboardModule,
    MarkdownModule,
    PopoverModule,
    TooltipModule,
    TsDocumentationsTabComponent,
    TicketDetailModalComponent,
    TimeEntryHistoryModalComponent,
    TimeEntryMetricsModalComponent,
    TicketLogCardComponent,
    TicketLogActiveFiltersComponent,
    TicketLogFilterPanelComponent,
    ContextMenuModule,
    ConfirmDialogModule,
    SavedFilterChipsComponent,
  ],
  exports: [
    SprintColumnComponent,
    SearchTicketByNumberWidgetComponent,
    NotificationsDropdownComponent,
  ],
  providers: [
    DatePipe,
    SortPipe,
    FilenamePipe,
    TicketDropService,
    DifficultyPipe,
    TicketLogTimePipe,
  ],
})
export class TicketCenterModule {}
